/** Price time-series chart for one date pair. Prop-driven, no fetching. */
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PriceSeries } from '../../types/flights';

export interface FlightPriceChartProps {
  series: PriceSeries[];
  currency?: string;
  height?: number;
}

const COLORS = ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2', '#be185d'];

export function FlightPriceChart({ series, currency = 'CHF', height = 280 }: FlightPriceChartProps) {
  const priced = series.filter((s) => s.points.length > 0);
  if (priced.length === 0) {
    return <p className="text-sm text-gray-500">No price history for this date pair yet.</p>;
  }

  // Merge all series onto a shared observed_at axis (day granularity).
  const days = Array.from(
    new Set(priced.flatMap((s) => s.points.map((p) => p.observed_at.slice(0, 10)))),
  ).sort();
  const data = days.map((day) => {
    const row: Record<string, string | number | null> = { day };
    for (const s of priced) {
      const pts = s.points.filter((p) => p.observed_at.slice(0, 10) === day);
      row[s.label] = pts.length ? Math.min(...pts.map((p) => p.price)) : null;
    }
    return row;
  });

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="day" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 11 }}
            domain={['auto', 'auto']}
            tickFormatter={(v: number) => `${v}`}
            label={{ value: currency, angle: -90, position: 'insideLeft', fontSize: 11 }}
          />
          <Tooltip formatter={(v: number) => [`${currency} ${v}`, undefined]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {priced.map((s, i) => (
            <Line
              key={s.label}
              type="monotone"
              dataKey={s.label}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              dot={{ r: 2.5 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
